import { TaskOutcomeDataDisplay } from './TaskOutcomeFields';
import { useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { T, useGT, Var } from 'gt-react';
import { AlarmIcon, ArrowUpLeftIcon } from '@phosphor-icons/react';
import { isTerminalTaskStatus } from '@erp/shared-types';
import type { TaskResponse, TaskTypeResponse } from '@erp/rpc';
import { rpcClient } from 'src/api/rpc/client';
import { invalidateTaskQueries } from 'src/api/rpc/hooks/tasks/invalidateTaskQueries';
import { Button } from 'src/components/ui/button';
import { toast } from 'src/components/ui/toast';
import {
  StatusPill,
  PriorityDot,
} from 'src/pages/authenticated/inbox/components/helpers';
import { TaskRelatedEntityBadge } from './TaskRelatedEntityBadge';
import { taskRelatedEntity } from './taskRelatedEntity';
import { taskDetailSearch } from '../taskDetailNav';
import { TaskActionsPanel } from './TaskActionsPanel';
import { SetTaskTypeDialog } from './SetTaskTypeDialog';
import { TeachRoutingButton } from './TeachRoutingDialog';
import { TaskAssigneeControl, TaskCloseControls } from './TaskReviewControls';
import { TaskDeletedBanner } from './TaskDeleteControls';
import { TaskHeaderMenu } from './TaskHeaderMenu';
import { taskTypeReplacesCloseControls } from './taskActionForm';
import { SnoozeTaskDialog } from '../SnoozeTaskDialog';
import { AgentRetrySnoozeText } from '../AgentRetrySnooze';
import { useAgentRetrySnooze } from '../useAgentRetrySnooze';
import {
  formatSnoozedUntil,
  isActivelySnoozed,
  isSnoozableTaskStatus,
} from '../taskSnooze';

/**
 * Shared review header: identity, related entity, assignee, type actions, and
 * close. Used on `/tasks/$taskId` and the agent-monitor rail.
 */
export function TaskReviewHeader({
  task,
  taskType,
  typeName,
  users,
  agents,
  fromPage,
  trailing,
  hideTitle,
}: {
  task: TaskResponse;
  taskType: TaskTypeResponse | undefined;
  typeName: string | undefined;
  users: Array<{ id: string; name: string }>;
  agents: Array<{ id: string; name: string }>;
  fromPage?: string;
  trailing?: ReactNode;
  /** The page header crumb already shows the title; drop the h1 and keep the pills. */
  hideTitle?: boolean;
}) {
  const terminal = isTerminalTaskStatus(task.status);
  // A soft-deleted task opened from a link is read-only: no toolbar.
  const deleted = task.deletedAt != null;

  return (
    <div className="rounded-md border border-border bg-white px-3 py-2.5">
      <TaskDeletedBanner task={task} />
      {!deleted && <TaskSnoozeBanner task={task} users={users} />}
      {terminal && (
        <TaskOutcomeDataDisplay
          data={task.outcomeData ?? null}
          fields={task.outcomeContractSnapshot?.fields ?? []}
          outcome={task.outcome ?? null}
        />
      )}
      <HeaderMeta
        displayId={task.displayId}
        typeName={typeName}
        {...taskRelatedEntity(task)}
        parentTaskId={task.parentTaskId}
        fromPage={fromPage}
        trailing={
          <div className="flex items-center gap-1">
            {trailing}
            <TaskHeaderMenu task={task} taskType={taskType} agents={agents} />
          </div>
        }
        badges={
          hideTitle ? (
            <TaskStateBadges
              priority={task.priority}
              status={task.status}
              outcome={terminal ? task.outcome : null}
              outcomeReason={terminal ? task.outcomeReason : null}
            />
          ) : undefined
        }
      />
      <TitleSection task={task} hideTitle={hideTitle} />
      {terminal || deleted ? (
        <ClosedFooter task={task} users={users} agents={agents} />
      ) : (
        <HeaderToolbar
          task={task}
          taskType={taskType}
          users={users}
          agents={agents}
        />
      )}
    </div>
  );
}

function HeaderMeta({
  displayId,
  typeName,
  anchorEntityType,
  anchorEntityId,
  parentTaskId,
  fromPage,
  trailing,
  badges,
}: {
  displayId: string | null;
  typeName: string | undefined;
  anchorEntityType: TaskResponse['anchorEntityType'];
  anchorEntityId: string | null;
  parentTaskId: string | null;
  fromPage?: string;
  trailing?: ReactNode;
  badges?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-[11px] text-muted-foreground">
      {displayId && (
        <span className="rounded bg-stone-100 px-1.5 py-px font-mono text-[11px] font-medium tabular-nums text-stone-600">
          {displayId}
        </span>
      )}
      {typeName && (
        <span className="rounded bg-stone-100 px-1.5 py-px text-[11px] text-stone-700">
          {typeName}
        </span>
      )}
      {anchorEntityType && anchorEntityId && (
        <span className="inline-flex min-w-0 items-center gap-1.5">
          <span className="shrink-0 text-[11px] text-muted-foreground">
            <T context="label before the linked business record on a task">
              Related to
            </T>
          </span>
          <TaskRelatedEntityBadge
            entityType={anchorEntityType}
            entityId={anchorEntityId}
          />
        </span>
      )}
      {parentTaskId && (
        <Link
          to="/tasks/$taskId"
          params={{ taskId: parentTaskId }}
          search={taskDetailSearch(fromPage)}
          className="inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline"
        >
          <ArrowUpLeftIcon className="size-3" />
          <T context="link from a subtask to its parent task">Parent task</T>
        </Link>
      )}
      {badges}
      {trailing && <div className="ml-auto shrink-0">{trailing}</div>}
    </div>
  );
}

function TaskStateBadges({
  priority,
  status,
  outcome,
  outcomeReason,
}: {
  priority: TaskResponse['priority'];
  status: TaskResponse['status'];
  outcome: string | null;
  outcomeReason: string | null;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="flex items-center gap-1.5 text-xs capitalize text-stone-700">
        <PriorityDot priority={priority} />
        {priority}
      </span>
      <StatusPill status={status} />
      {outcome && (
        <span
          className="rounded-full border border-stone-300 bg-stone-100 px-2 py-0.5 text-[11px] font-medium capitalize text-stone-700"
          title={outcomeReason ?? undefined}
        >
          {outcome}
        </span>
      )}
    </div>
  );
}

function HeaderTitle({
  title,
  priority,
  status,
  outcome,
  outcomeReason,
}: {
  title: string;
  priority: TaskResponse['priority'];
  status: TaskResponse['status'];
  outcome: string | null;
  outcomeReason: string | null;
}) {
  return (
    <>
      <div className="mt-1.5 flex flex-wrap items-start gap-x-3 gap-y-1.5">
        <h1 className="min-w-0 flex-1 text-base font-semibold leading-snug text-stone-900">
          {title}
        </h1>
        <TaskStateBadges
          priority={priority}
          status={status}
          outcome={outcome}
          outcomeReason={outcomeReason}
        />
      </div>
      {outcomeReason && (
        <p className="mt-1.5 text-xs text-stone-600">{outcomeReason}</p>
      )}
    </>
  );
}

function HeaderToolbar({
  task,
  taskType,
  users,
  agents,
}: {
  task: TaskResponse;
  taskType: TaskTypeResponse | undefined;
  users: Array<{ id: string; name: string }>;
  agents: Array<{ id: string; name: string }>;
}) {
  return (
    <div className="mt-2.5 flex flex-wrap items-center justify-end gap-2 border-t border-border pt-2.5">
      <div className="w-full min-w-[12rem] max-w-xs sm:w-auto sm:flex-none">
        <TaskAssigneeControl task={task} users={users} agents={agents} />
      </div>
      <div className="mx-0.5 hidden h-4 w-px shrink-0 bg-border sm:block" />
      <TaskSnoozeButton task={task} />
      <TeachRoutingButton task={task} />
      {!task.taskTypeId && <SetTaskTypeDialog task={task} />}
      {taskType && <TaskActionsPanel task={task} taskType={taskType} />}
      {!taskTypeReplacesCloseControls(taskType?.actions) && (
        <TaskCloseControls task={task} />
      )}
    </div>
  );
}

function TaskSnoozeBanner({
  task,
  users,
}: {
  task: TaskResponse;
  users: Array<{ id: string; name: string }>;
}) {
  const gt = useGT();
  const queryClient = useQueryClient();
  const until = task.snoozedUntil;
  const active = until != null && isActivelySnoozed(until);
  const snoozedBy = users.find(
    (user) => user.id === task.snoozedByUserId,
  )?.name;
  const agentRetry = useAgentRetrySnooze(
    task.id,
    until,
    active && !task.snoozedByUserId,
  );
  const clear = useMutation({
    mutationFn: () => rpcClient.tasks.clearSnooze({ taskIds: [task.id] }),
    onSuccess: () => {
      invalidateTaskQueries(queryClient);
      toast.success(
        gt('Snooze cleared', {
          context: 'toast after clearing a task snooze from the task page',
        }),
      );
    },
    onError: (error) => {
      toast.error(
        error instanceof Error
          ? error.message
          : gt('Could not clear the snooze', {
              context: 'toast after clearing a task snooze failed',
            }),
      );
    },
  });

  if (!active || until == null) return null;
  const when = formatSnoozedUntil(until);

  return (
    <div className="mb-2 flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-950">
      <AlarmIcon className="size-4 shrink-0" />
      <span className="min-w-0 flex-1">
        {agentRetry ? (
          <AgentRetrySnoozeText retry={agentRetry} />
        ) : snoozedBy ? (
          <T context="task page banner: this task is hidden from lists until a time, and who snoozed it">
            Snoozed until <Var>{when}</Var> by <Var>{snoozedBy}</Var>
          </T>
        ) : (
          <T context="task page banner: this task is hidden from lists until a time">
            Snoozed until <Var>{when}</Var>
          </T>
        )}
      </span>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={clear.isPending}
        onClick={() => clear.mutate()}
      >
        <AlarmIcon className="size-4" />
        <T context="button that ends the current snooze so the task returns to lists">
          Clear Snooze
        </T>
      </Button>
    </div>
  );
}

function TaskSnoozeButton({ task }: { task: TaskResponse }) {
  const [open, setOpen] = useState(false);
  if (!isSnoozableTaskStatus(task.status)) return null;
  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
      >
        <AlarmIcon className="size-4" />
        <T context="button on the task page that opens the snooze dialog">
          Snooze
        </T>
      </Button>
      {open && (
        <SnoozeTaskDialog taskIds={[task.id]} open onOpenChange={setOpen} />
      )}
    </>
  );
}

function TitleSection({
  task,
  hideTitle,
}: {
  task: TaskResponse;
  hideTitle?: boolean;
}) {
  const terminal = isTerminalTaskStatus(task.status);
  if (hideTitle)
    return (
      terminal &&
      task.outcomeReason && (
        <p className="mt-1.5 text-xs text-stone-600">{task.outcomeReason}</p>
      )
    );
  return (
    <HeaderTitle
      title={task.title}
      priority={task.priority}
      status={task.status}
      outcome={terminal ? task.outcome : null}
      outcomeReason={terminal ? task.outcomeReason : null}
    />
  );
}

function ClosedFooter({
  task,
  users,
  agents,
}: {
  task: TaskResponse;
  users: Array<{ id: string; name: string }>;
  agents: Array<{ id: string; name: string }>;
}) {
  const gt = useGT();
  const assigneeLine = closedTaskAssigneeLabel({
    assigneeAgentDefinitionId: task.assigneeAgentDefinitionId,
    assigneeUserId: task.assigneeUserId,
    agentName: agents.find((a) => a.id === task.assigneeAgentDefinitionId)
      ?.name,
    userName: users.find((u) => u.id === task.assigneeUserId)?.name,
    agentFallback: gt('Assigned agent', {
      context: 'task assignee fallback label',
    }),
    userFallback: gt('Assigned', { context: 'task assignee fallback label' }),
    unassigned: gt('Unassigned', {
      context: 'task assignee label, nobody assigned',
    }),
  });

  return (
    <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
      <p className="text-[11px] text-muted-foreground">
        {gt('Assignee: {name}', {
          context: 'closed task assignee line on review header',
          name: assigneeLine,
        })}
      </p>
      {/* Closed untyped tasks can still be typed (history/analytics);
          the service allows it, so the header must offer it too. A deleted
          task is read-only. */}
      {!task.taskTypeId && !task.deletedAt && <SetTaskTypeDialog task={task} />}
    </div>
  );
}

function closedTaskAssigneeLabel({
  assigneeAgentDefinitionId,
  assigneeUserId,
  agentName,
  userName,
  agentFallback,
  userFallback,
  unassigned,
}: {
  assigneeAgentDefinitionId: string | null;
  assigneeUserId: string | null;
  agentName: string | undefined;
  userName: string | undefined;
  agentFallback: string;
  userFallback: string;
  unassigned: string;
}): string {
  if (assigneeAgentDefinitionId) return agentName ?? agentFallback;
  if (assigneeUserId) return userName ?? userFallback;
  return unassigned;
}
